import { reactive } from 'vue';
import {
    getTokenParsed,
    initKeycloak,
    isSkipAuth,
    login as kcLogin,
    logout as kcLogout,
} from './keycloak.service';
import apiClient from './api/api-client';

export const state = reactive({
    profile: null,
    isAuthenticated: false,
    isLoggingOut: false,
});

export const getters = {
    get isAuthenticated() {
        return state.isAuthenticated;
    },
    get isLoggingOut() {
        return state.isLoggingOut;
    },
    get profile() {
        return state.profile;
    },
    get roles() {
        return state.profile?.roles || [];
    },
    get orgId() {
        return state.profile?.orgId || null;
    },
    hasRole(role) {
        return getters.roles.includes(role.toLowerCase());
    },
};

function splitDisplayName(displayName) {
    if (!displayName) {return { firstName: '', lastName: '' };}
    const space = displayName.indexOf(' ');

    if (space < 0) {return { firstName: displayName, lastName: '' };}

    return {
        firstName: displayName.slice(0, space),
        lastName: displayName.slice(space + 1),
    };
}

function profileFromBackend(response) {
    // The backend now returns firstName/lastName explicitly; fall back to
    // splitting displayName for older responses that only carry the full name.
    const split = splitDisplayName(response.displayName);
    const firstName = response.firstName ?? split.firstName;
    const lastName = response.lastName ?? split.lastName;

    return {
        sub: response.sub,
        email: response.email,
        username: response.email,
        firstName,
        lastName,
        displayName: response.displayName || response.email,
        address: response.address || '',
        country: response.country || '',
        roles: (response.roles || []).map((r) => r.toLowerCase()),
        orgId: response.orgKey || null,
    };
}

function profileFromToken(tokenParsed) {
    return {
        sub: tokenParsed.sub,
        email: tokenParsed.email,
        username: tokenParsed.preferred_username,
        firstName: tokenParsed.given_name || '',
        lastName: tokenParsed.family_name || '',
        displayName:
            tokenParsed.name ||
            tokenParsed.preferred_username ||
            tokenParsed.email,
        roles: tokenParsed.realm_access?.roles || [],
        orgId: tokenParsed.orgId || null,
    };
}

export async function loadProfile() {
    try {
        const response = await apiClient.get('/api/v1/me');

        return profileFromBackend(response);
    } catch (err) {
        console.warn('Falling back to JWT-derived profile; /me failed', err);

        const tokenParsed = getTokenParsed();

        return tokenParsed ? profileFromToken(tokenParsed) : null;
    }
}

/**
 * Re-reads the current user's profile from the backend and updates the shared
 * state, so views like the sidebar reflect edits made on the account page.
 */
export async function refreshProfile() {
    if (isSkipAuth()) {return state.profile;}

    state.profile = await loadProfile();
    state.isAuthenticated = state.profile !== null;

    return state.profile;
}

export async function checkSession() {
    if (isSkipAuth()) {
        state.profile = {
            sub: 'dev-user',
            email: 'dev@hive.local',
            username: 'dev',
            firstName: 'Dev',
            lastName: 'User',
            displayName: 'Dev User',
            roles: ['admin'],
            orgId: 'dev-org',
        };
        state.isAuthenticated = true;

        return state.profile;
    }

    await initKeycloak();
    const tokenParsed = getTokenParsed();

    if (!tokenParsed) {
        state.profile = null;
        state.isAuthenticated = false;

        return null;
    }

    state.profile = await loadProfile();
    state.isAuthenticated = state.profile !== null;

    return state.profile;
}

export function login() {
    return kcLogin();
}

export function logout() {
    state.isLoggingOut = true;

    return kcLogout().catch((err) => {
        state.isLoggingOut = false;
        throw err;
    });
}
